import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { last } from "lodash";
import SourceName from "../../../SourceName";
import CalculationStatusIcon from "../../../CalculationStatusIcon";
import TextTruncate from "components/TextTruncate";
import IconButton from "components/IconButton";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import DefaultThreeDotsMenuItem from "components/ThreeDotsMenu/Components/DefaultThreeDotsMenuItem";
import ThreeDotsMenu from "components/ThreeDotsMenu";
import LineTypeIndicator from "../../../LineTypeIndicator";
import { viewTypeOptions } from "../SourceRow";
import { MAX_VIEWABLE_SOURCES } from "../Sources";
import { preparePlotLayout } from "../../../GraphElementGraph/utils";
import { prepareErrorsGraphPlotLayout } from "../../../GraphElementErrorsGraph/utils";
import GraphElementErrorsGraph from "../../../GraphElementErrorsGraph";
import CreateEntityFromResultElementDialog from "pages/RS-Workspace/components/Results/components/CreateEntityFromResultElementDialog";
import CreateVariantFromResultElementDialog from "pages/RS-Workspace/components/Results/components/CreateVariantFromResultElementDialog";
import BiasedIcon from "../../../BiasedIcon";
import { CalculationStatusSource, EntitiesCreatableFromResultElement, ResultElementSourceAppearance, ResultElementSourceType, ResultSetElementType, runsCountValue, SimulationRunState, } from "store/rs-workspace/rs-workspace.types";
import { SourceFields } from "../../types";
import { calculationElementRowSelector, calculationGraphElementSourceResultSelector, createEntityFromResultElementDialogDataSelector, getActiveResultSetIdSelector, processDataViewerGraphErrorsPointsSelector, processDataViewerSourceFormValuesSelector, } from "store/rs-workspace/selectors/results.selectors";
import { workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { setCreateEntityFromResultElementDialogData, setProcessDataViewerSourceParams, } from "store/rs-workspace/actions/results.actions";
import { getSourceLineColor, getSourceNameTooltipLabel, prepareProcessDataViewerGraphErrorsData, } from "store/rs-workspace/utils/results.utils";
import { INPUT, NewEntityCreationOption } from "shared/labels";
import { getTimeUnit } from "utils/dateTime";
import IcNotVisible from "icons/icn-not-visible.svg";
import IcVisible from "icons/icn-visible.svg";
import AutoscaleIcon from "icons/autoscale.svg";
import UnlockIcon from "icons/open-lock.svg";
import LockIcon from "icons/lock.svg";
import PlusIcon from "icons/plus.svg";
import * as styles from "../../styles.module.scss";
const SourceRowPreview = ({ elementData, source, sources, axisName, index, rangeConfig: { autorange, fixedrange } = {}, handleAutoscaleButtonClick, onLockButtonClick, }) => {
    const dispatch = useDispatch();
    const [errorsGraphOpen, setErrorsGraphOpen] = useState(false);
    const [errorsPlotLayout, setErrorsPlotLayout] = useState(null);
    const [threeDotsMenuOpen, setThreeDotsMenuOpen] = useState(false);
    const [newEntityType, setNewEntityType] = useState(null);
    const pageInEditMode = useSelector(isEditableSelector);
    const resultSetId = useSelector(getActiveResultSetIdSelector);
    const createEntityDialogData = useSelector(createEntityFromResultElementDialogDataSelector);
    const { preparedSource, isCompleted, isFilled } = useSelector(processDataViewerSourceFormValuesSelector(source));
    const simulations = useSelector(workspaceSimulationsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const sourceResult = useSelector(calculationGraphElementSourceResultSelector({ resultSetId, resultSetElementId: elementData.id }, elementData.type, source.id));
    const sourceCalculationRow = useSelector(calculationElementRowSelector({ resultSetId, resultSetElementId: elementData.id }, source.id));
    const points = useSelector(processDataViewerGraphErrorsPointsSelector({ resultSetId, resultSetElementId: elementData.id }, source));
    const errorsGraphData = useMemo(() => prepareProcessDataViewerGraphErrorsData(points, source, index), [points, index, source]);
    const timeUnit = useMemo(() => getTimeUnit(last(sourceResult?.points)?.point?.x ?? 1), [sourceResult?.points]);
    const isInsight = useMemo(() => preparedSource?.source?.type === ResultElementSourceType.Insight, [preparedSource?.source]);
    const isProcessData = useMemo(() => preparedSource?.source?.type === ResultElementSourceType.ProcessData, [preparedSource?.source]);
    const halfWidthSourceSubtitle = useMemo(() => {
        const variable = preparedSource?.source?._variableName?.label;
        const units = preparedSource?.source?.units?.label;
        return `${variable} ${units ? `(${units})` : ""}`;
    }, [preparedSource?.source]);
    const turnOnSourceViewDisabled = useMemo(() => !preparedSource?.view && sources.filter(({ view }) => Boolean(view))?.length >= MAX_VIEWABLE_SOURCES, [sources, preparedSource?.view]);
    const appearance = useMemo(() => {
        const { state, points, fetching } = { ...sourceResult };
        const calculationStatus = sourceCalculationRow.status;
        if (!isCompleted) {
            return ResultElementSourceAppearance.Incomplete;
        }
        if (fetching || calculationStatus === CalculationStatusSource.InProgress) {
            return ResultElementSourceAppearance.InProgress;
        }
        if (calculationStatus === CalculationStatusSource.Paused) {
            return ResultElementSourceAppearance.Paused;
        }
        if (state === SimulationRunState.ERROR) {
            return ResultElementSourceAppearance.Error;
        }
        if (!!points?.length || state === SimulationRunState.FINISHED) {
            return ResultElementSourceAppearance.Calculated;
        }
        return ResultElementSourceAppearance.Ready;
    }, [sourceResult, sourceCalculationRow.status, isCompleted]);
    const tooltip = useMemo(() => {
        const { type = ResultElementSourceType.Simulation } = { ...(source.source ?? {}) };
        const typeLabel = type === ResultElementSourceType.Simulation
            ? "Simulation"
            : type === ResultElementSourceType.Insight
                ? "Insight"
                : "Process Data";
        const tooltipLabel = getSourceNameTooltipLabel(typeLabel, source.source, appearance, simulations, cellModels, recipes);
        switch (appearance) {
            case ResultElementSourceAppearance.Ready:
                return isFilled ? "Ready to be calculated" : undefined;
            case ResultElementSourceAppearance.Incomplete: {
                return `The ${tooltipLabel} is no longer valid`;
            }
            case ResultElementSourceAppearance.Error:
                return sourceResult?.error || `${tooltipLabel} can not be calculated.`;
        }
    }, [appearance, isFilled, source.source, sourceResult?.error, simulations, cellModels, recipes]);
    const totalRunsCount = useMemo(() => {
        const { uncertainty, variability } = sourceResult?.accuracy ?? {};
        if (!uncertainty || !variability) {
            return 1;
        }
        return runsCountValue[uncertainty] * runsCountValue[variability];
    }, [sourceResult?.accuracy]);
    const canCreateEntityFromSource = useMemo(() => !!sourceResult?.points &&
        sourceResult?.points?.length > 0 &&
        ((source.source?.type === ResultElementSourceType.Simulation &&
            !!source.source?.adjustmentId) ||
            !!source.source?.optimizationId), [source.source, sourceResult?.points]);
    const menuItems = useMemo(() => [
        ...(source.source?.optimizationId
            ? [
                {
                    text: NewEntityCreationOption.Variant,
                    icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
                    onClick: () => {
                        setNewEntityType(EntitiesCreatableFromResultElement.Variant);
                        dispatch(setCreateEntityFromResultElementDialogData({
                            elementId: elementData.id,
                            sourceId: source.id ?? 0,
                        }));
                    },
                },
            ]
            : []),
        {
            text: NewEntityCreationOption.Recipe,
            icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
            onClick: () => {
                setNewEntityType(EntitiesCreatableFromResultElement.Recipe);
                dispatch(setCreateEntityFromResultElementDialogData({
                    elementId: elementData.id,
                    sourceId: source.id ?? 0,
                }));
            },
        },
        {
            text: NewEntityCreationOption.CellModel,
            icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
            onClick: () => {
                setNewEntityType(EntitiesCreatableFromResultElement.CellModel);
                dispatch(setCreateEntityFromResultElementDialogData({
                    elementId: elementData.id,
                    sourceId: source.id ?? 0,
                }));
            },
        },
    ], [dispatch, elementData.id, source.id, source.source]);
    const renderCreateEntityDialogs = useMemo(() => {
        if (createEntityDialogData.elementId !== elementData.id ||
            createEntityDialogData.sourceId !== source.id) {
            return null;
        }
        return newEntityType === EntitiesCreatableFromResultElement.Variant ? (React.createElement(CreateVariantFromResultElementDialog, { simulationId: source.source.simulationId ?? 0, adjustmentId: source.source.adjustmentId ?? 0, sourceId: source.id ?? 0, resultElementType: ResultSetElementType.ProcessDataViewer, elementId: elementData.id })) : (React.createElement(CreateEntityFromResultElementDialog, { isOpen: true, sourceId: source.id, entityType: newEntityType ?? EntitiesCreatableFromResultElement.Recipe, resultElementType: ResultSetElementType.ProcessDataViewer, elementId: elementData.id, entityId: source.source.simulationId ?? 0, onClose: () => dispatch(setCreateEntityFromResultElementDialogData({ elementId: null, sourceId: null })) }));
    }, [
        createEntityDialogData.elementId,
        createEntityDialogData.sourceId,
        dispatch,
        elementData.id,
        newEntityType,
        source.id,
        source.source,
    ]);
    const onChangeSourceParams = useCallback((option, fieldName) => {
        if (!resultSetId || !source?.id)
            return;
        dispatch(setProcessDataViewerSourceParams({
            resultSetId,
            elementId: elementData.id,
            sourceId: source?.id,
            lineType: source?.lineType,
            view: source?.view,
            viewType: source?.viewType,
            color: source?.color,
            [fieldName]: option?.value,
        }));
    }, [dispatch, resultSetId, source, elementData.id]);
    useEffect(() => {
        if (errorsPlotLayout === null) {
            setErrorsPlotLayout({
                ...preparePlotLayout(sourceResult ? [sourceResult] : [], elementData.xPlotConfig, undefined, {
                    xaxis: { title: timeUnit ? `Time (${timeUnit})` : "Time" },
                    yaxis: { fixedrange: false },
                    yaxis2: { fixedrange: false },
                }),
                ...prepareErrorsGraphPlotLayout(sourceResult || {}, ResultSetElementType.ProcessDataViewer, totalRunsCount),
            });
        }
    }, [elementData.xPlotConfig, errorsPlotLayout, sourceResult, timeUnit, totalRunsCount]);
    return (React.createElement("div", { className: classNames(styles.row, {
            [styles.faded]: !preparedSource.view,
            [styles.errored]: !!sourceResult?.error && appearance === ResultElementSourceAppearance.Error,
        }) },
        React.createElement("div", { className: classNames(styles.cell, styles.view) },
            React.createElement(IconButton, { tabIndex: -1, title: "View", disabled: turnOnSourceViewDisabled, classes: { root: styles.iconButton }, icon: preparedSource?.view ? React.createElement(IcVisible, null) : React.createElement(IcNotVisible, null), onClick: () => !turnOnSourceViewDisabled &&
                    onChangeSourceParams({ value: !preparedSource?.view, label: "" }, SourceFields.view) })),
        React.createElement(SourceName, { className: classNames(styles.cell, styles.source), tooltip: sourceResult?.error || tooltip },
            React.createElement("div", { className: styles["source-container"] },
                React.createElement(CalculationStatusIcon, { accuracy: sourceResult?.accuracy || null, appearance: appearance, className: styles["status-icon"], hideAccuracy: isProcessData }),
                React.createElement("div", null,
                    React.createElement(TextTruncate, { text: preparedSource?.source?._sourceName || INPUT.UNDEFINED, className: classNames(styles["preview-value"], {
                            [styles.regularHeight]: !!halfWidthSourceSubtitle && !elementData.fullView && !isInsight,
                            [styles.accented]: appearance === ResultElementSourceAppearance.Incomplete ||
                                appearance === ResultElementSourceAppearance.Error,
                        }) }),
                    !elementData.fullView && !isInsight && (React.createElement(TextTruncate, { text: halfWidthSourceSubtitle, className: styles["subtitle"] }))))),
        elementData.fullView && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classNames(styles.cell, styles.variable) }, !isInsight && (React.createElement(TextTruncate, { text: preparedSource?.source?._variableName?.label, className: styles["preview-value"] }))),
            React.createElement("div", { className: classNames(styles.cell, styles.unit) }, !isInsight && (React.createElement(TextTruncate, { text: preparedSource?.source?.units?.label, className: styles["preview-value"] }))))),
        React.createElement("div", { className: classNames(styles.cell, styles.line) },
            React.createElement(LineTypeIndicator, { color: getSourceLineColor(preparedSource?.color, index), onChange: (color) => onChangeSourceParams({ value: color, label: "" }, SourceFields.color) })),
        React.createElement("div", { className: classNames(styles.cell, styles.viewType) }, !isProcessData && (React.createElement(SelectInput, { items: viewTypeOptions, itemRenderer: SelectItemRenderer, className: styles.select, placeholder: "Select", value: preparedSource?.viewType, onChange: (option) => onChangeSourceParams(option, SourceFields.viewType) }))),
        React.createElement("div", { className: classNames(styles.cell, styles.autoscale) },
            React.createElement("div", { className: styles["icon-container"] }, !!sourceResult?.hasErrors && (React.createElement(BiasedIcon, { errorsPlotLayout: errorsPlotLayout, firstError: sourceResult?.errors?.[0], totalRunsCount: totalRunsCount, errorCount: last(sourceResult?.points)?.errorCount, sourceView: !!source.view, onOpen: setErrorsGraphOpen, setErrorsPlotLayout: setErrorsPlotLayout }))),
            React.createElement("div", { className: styles["icon-container"] },
                React.createElement(IconButton, { tabIndex: -1, classes: { root: classNames(styles.scaleIcon, { [styles.iconfill]: autorange }) }, icon: React.createElement(AutoscaleIcon, null), title: "Autoscale", onClick: () => handleAutoscaleButtonClick && handleAutoscaleButtonClick(axisName, source.id), disabled: !source.view })),
            React.createElement("div", { className: styles["icon-container"] },
                React.createElement(IconButton, { tabIndex: -1, icon: fixedrange ? React.createElement(LockIcon, null) : React.createElement(UnlockIcon, null), title: fixedrange ? "Unlock axis" : "Lock axis", onClick: () => onLockButtonClick && onLockButtonClick(!fixedrange, axisName, source.id), disabled: !source.view })),
            React.createElement("div", { className: styles["icon-container"] }, pageInEditMode && canCreateEntityFromSource && (React.createElement(ThreeDotsMenu, { name: "Actions", classes: {
                    className: classNames(styles.threeDotsMenu, {
                        [styles["threeDotsMenu-opened"]]: threeDotsMenuOpen,
                    }),
                }, items: menuItems.map((item, index) => (React.createElement(DefaultThreeDotsMenuItem, { key: index, closeMenuOnClick: () => setThreeDotsMenuOpen(false), ...item }))), isOpen: threeDotsMenuOpen, setIsOpen: setThreeDotsMenuOpen })))),
        errorsPlotLayout !== null && (React.createElement(GraphElementErrorsGraph, { isOpen: errorsGraphOpen, onClose: () => setErrorsGraphOpen(false), preparedSource: preparedSource, elementData: elementData, graphData: errorsGraphData ?? [], plotLayout: errorsPlotLayout, timeUnit: timeUnit, error: sourceResult?.errors[0] })),
        renderCreateEntityDialogs));
};
export default SourceRowPreview;
